import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api";
import {
  deletePushSubscription,
  listPushSubscriptions,
  type PushSubscriptionView,
} from "@/lib/notifications-api";

/**
 * apps/web :: components :: PushDevicesList
 *
 * Rendered beneath the InAppPush toggle in NotificationPreferences ONLY
 * when push is enabled for the caller. Each row:
 *
 *   "Chrome on macOS • last used 2h ago    [Remove]"
 *
 * Owns its own load/remove lifecycle — NotificationPreferences signals a
 * reload via the `refreshKey` prop (bump on subscribe/unsubscribe) so the
 * list stays in sync without the parent owning the list state.
 */
interface Props {
  /** Bumped by the parent whenever the subscriptions set may have changed. */
  refreshKey: number;
  /** Notify parent when we successfully delete — so it can recompute state. */
  onAfterRemove?: () => void;
}

interface LoadState {
  kind: "loading" | "ready" | "error";
  errorMessage?: string;
  items: PushSubscriptionView[];
}

export default function PushDevicesList({ refreshKey, onAfterRemove }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>({ kind: "loading", items: [] });
  const [removingId, setRemovingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setState((prev) => ({ kind: "loading", items: prev.items }));
    try {
      const items = await listPushSubscriptions();
      // Most-recent first — `lastUsedAtMs` is initially set to createdAt by
      // the backend, so brand-new subscriptions sort to the top.
      items.sort((a, b) => b.lastUsedAtMs - a.lastUsedAtMs);
      setState({ kind: "ready", items });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setState({ kind: "error", errorMessage: msg, items: [] });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, refreshKey]);

  const handleRemove = useCallback(
    async (id: string) => {
      setRemovingId(id);
      try {
        await deletePushSubscription(id);
        await refresh();
        onAfterRemove?.();
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setState((prev) => ({
          kind: "error",
          errorMessage: msg,
          items: prev.items,
        }));
      } finally {
        setRemovingId(null);
      }
    },
    [refresh, onAfterRemove],
  );

  if (state.kind === "loading" && state.items.length === 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="mt-sm text-xs text-neutral-600"
        data-testid="push-devices-loading"
      >
        {t("notifications.devices.loading")}
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div
        role="alert"
        className="mt-sm rounded-md border border-danger/40 bg-danger/5 p-sm text-xs text-danger"
        data-testid="push-devices-error"
      >
        {t("notifications.devices.loadError", { message: state.errorMessage ?? "" })}
      </div>
    );
  }

  if (state.items.length === 0) {
    return (
      <p
        className="mt-sm text-xs text-neutral-600"
        data-testid="push-devices-empty"
      >
        {t("notifications.devices.empty")}
      </p>
    );
  }

  return (
    <ul
      className="mt-sm space-y-xs"
      data-testid="push-devices-list"
      aria-label={t("notifications.devices.ariaLabel")}
    >
      {state.items.map((sub) => (
        <li
          key={sub.id}
          data-testid={`push-device-row-${sub.id}`}
          className="flex items-center justify-between gap-sm rounded-md border border-brand-primary/10 bg-white px-sm py-xs text-xs"
        >
          <span className="flex-1 text-brand-primary">
            <span className="font-medium">
              {sub.deviceLabel || t("notifications.devices.unknownDevice")}
            </span>
            <span className="ml-xs text-neutral-600">
              {t("notifications.devices.lastUsed", {
                relative: formatRelative(sub.lastUsedAtMs, t),
              })}
            </span>
          </span>
          <button
            type="button"
            onClick={() => void handleRemove(sub.id)}
            disabled={removingId === sub.id}
            data-testid={`push-device-remove-${sub.id}`}
            className="rounded-md border border-danger/40 px-sm py-xs text-danger hover:bg-danger/10 disabled:opacity-50"
          >
            {removingId === sub.id
              ? t("notifications.devices.removing")
              : t("notifications.devices.remove")}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Coarse "2h ago" / "3d ago" formatter. Not i18n-perfect (no Intl.RelativeTimeFormat
 * plural logic) but good enough for a device list — callers can upgrade later.
 */
function formatRelative(
  epochMs: number,
  t: (k: string, v?: Record<string, unknown>) => string,
): string {
  const now = Date.now();
  const delta = Math.max(0, now - epochMs);
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return t("notifications.devices.justNow");
  if (minutes < 60) return t("notifications.devices.minutesAgo", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("notifications.devices.hoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  return t("notifications.devices.daysAgo", { count: days });
}
